import Button from '@base/Button/Button';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import Image from '@base/Image';
import Link from '@base/Link/Link';
import P1 from '@base/P1/P1';
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import { POPULAR_SUBJECT_SELECTED, SEE_ALL_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Typography from 'aquilla/core/atoms/Typography';

const Wrapper = styled.div`
  grid-column: span 18;
  margin-bottom: 80px;
`;

const LabelBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
`;

const SubjectsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(18, 32px);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  grid-auto-rows: max-content;
  ${mediaQuery.uptoTablet} {
    overflow: scroll;
    display: ${({ $isFeed }) => ($isFeed ? 'flex' : 'block')};
    flex-wrap: wrap;
  }
`;

const SeeAll = styled(Button)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    min-height: 40px;
    height: 40px;
    width: 100%;
    background: var(--color-i-white);
    border: 1px solid var(--color-divider);
    border-radius: 8px;
    color: var(--color-i-green);
    font-size: ${Fonts.SMALL};
    margin: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_14B};
  }
`;

const Subject = styled(Link)`
  grid-column: span 6;
  padding: ${Spacings.SPACING_6B};
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  border-radius: 8px;
  margin-top: ${Spacings.SPACING_2B};
  display: flex;
  align-items: center;
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_4B};
    width: ${({ $isFeed }) => ($isFeed ? '47.5%' : '100%')};
  }
`;

const StyledH6 = styled(H6)`
  margin-left: ${Spacings.SPACING_4B};
`;

const ImageDiv = styled.div`
  width: ${Spacings.SPACING_10B};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LabelDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;
const InfoSectionsDiv = styled.div``;

const Description = styled(Typography)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
`;

const PopularSubjects = ({ feedBlock, isFeed = true }) => {
  const { query } = useRouter();
  const { isMobile } = useWindowSize();
  const viewAllHref = '/goal/[goalSlug]/[goalUID]/classes/[type]';
  const viewAllLink = `/goal/${query.goalSlug}/${query.goalUID}/classes/popular_subjects`;

  const goalProps = useGoalEventProps(query.goalUID);

  const onClickSeeAll = () => {
    segment.track(SEE_ALL_CLICKED, {
      ...goalProps,
      'Last Primary Source Section': 'Popular subject'
    });
    pushRoute(viewAllHref, viewAllLink);
  };

  const onClickSubject = (subLabel) => {
    segment.track(POPULAR_SUBJECT_SELECTED, {
      ...goalProps,
      subject_selected: subLabel
    });
  };

  const showSeeAll = feedBlock?.extraBlockinfo?.count > 6;

  return (
    <Wrapper>
      {isFeed && (
        <LabelBlock>
          <InfoSectionsDiv>
            <H4>{feedBlock.label}</H4>
            {feedBlock?.description && (
              <Description variant="p2">{feedBlock.description}</Description>
            )}
          </InfoSectionsDiv>
          {!isMobile && showSeeAll && (
            <Link href={viewAllHref} as={viewAllLink}>
              <BoldP1 color="var(--color-i-green)">SEE ALL</BoldP1>
            </Link>
          )}
        </LabelBlock>
      )}
      <SubjectsWrapper $isFeed={isFeed}>
        {feedBlock.data.map((data) => {
          const icon = `subjects-icons/Type-${data.extra_info.label.slice(
            0,
            1
          )}.svg`;
          return (
            <Subject
              $isFeed={isFeed}
              key={`data-${data.data[0].uid}`}
              href="/goal/[goalSlug]/[goalUID]/classes/all"
              as={`/goal/${query.goalSlug}/${query.goalUID}/classes/all?subject=${data.data[0].uid}`}
              onClick={() => onClickSubject(data.extra_info.label)}
            >
              <ImageDiv>
                <Image src={icon} width={32} height={32} alt="subject-icon" />
              </ImageDiv>
              <LabelDiv>
                <StyledH6>{data.extra_info.label}</StyledH6>
              </LabelDiv>
            </Subject>
          );
        })}
      </SubjectsWrapper>
      {isFeed && showSeeAll && isMobile ? (
        <SeeAll label="See All" onClick={onClickSeeAll} />
      ) : null}
    </Wrapper>
  );
};

export default PopularSubjects;
